import { Component } from '@theme/component';
import { SlideshowSelectEvent, ThemeEvents } from '@theme/events';

/* ↗ .cursor/references/code-notes/r-adaptive-contrast.js.md */

const WORDMARK_SELECTOR = '.r-header-statement-logo';
const ART_SELECTOR = '.r-header-statement-logo__link';
const GALLERY_SELECTOR = '.r-pdp-gallery slideshow-component';
const SCROLLER_SELECTOR = 'slideshow-slides';
const CURRENT_SLIDE_SELECTOR = 'slideshow-slide[aria-hidden="false"]';
const SLIDE_IMAGE_SELECTOR = 'img';

const ON_DARK = 'on-dark';
const ON_LIGHT = 'on-light';

/** Side of the square the decoded bitmap is reduced to; every probe reads from it. */
const THUMB_SIZE = 64;
/** Decoded thumbnails kept, by source URL. A PDP has a handful of slides. */
const THUMB_CACHE_MAX = 12;
/** Luminance at or above which a sample reads as paper — the pale studio wall of a pack shot. */
const PAPER_LUMINANCE = 0.85;
/** Share of paper samples that flips the mark to green, and the lower share it must fall below to go back. */
const PAPER_SHARE_ENTER = 0.75;
const PAPER_SHARE_LEAVE = 0.6;
/** Probe grid over the mark's box. */
const ROWS = [0.2, 0.5, 0.8];
const MAX_COLUMNS = 7;

/** @type {Map<string, Uint8ClampedArray | Promise<Uint8ClampedArray | null>>} */
const thumbnails = new Map();

class AdaptiveContrast extends Component {
  requiredRefs = [];

  /** @type {number | null} */
  #frame = null;
  /** @type {ReturnType<typeof setTimeout> | null} */
  #resizeTimer = null;
  #pendingWhileHidden = false;

  connectedCallback() {
    super.connectedCallback();
    addEventListener(SlideshowSelectEvent.eventName, this.#onSlide, { passive: true });
    addEventListener(ThemeEvents.variantUpdate, this.#schedule, { passive: true });
    addEventListener('load', this.#onLoad, { capture: true, passive: true });
    addEventListener('resize', this.#onResize, { passive: true });
    document.addEventListener('visibilitychange', this.#onVisibilityChange);
    this.#schedule();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    removeEventListener(SlideshowSelectEvent.eventName, this.#onSlide);
    removeEventListener(ThemeEvents.variantUpdate, this.#schedule);
    removeEventListener('load', this.#onLoad, { capture: true });
    removeEventListener('resize', this.#onResize);
    document.removeEventListener('visibilitychange', this.#onVisibilityChange);
    if (this.#frame != null) cancelAnimationFrame(this.#frame);
    if (this.#resizeTimer != null) clearTimeout(this.#resizeTimer);
  }

  get #wordmark() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component')?.querySelector(WORDMARK_SELECTOR) ?? null);
  }

  get #gallery() {
    return document.querySelector(GALLERY_SELECTOR);
  }

  /** @param {Event} event */
  #onSlide = (event) => {
    const gallery = this.#gallery;
    if (gallery && event.target instanceof Node && gallery.contains(event.target)) {
      const slide = /** @type {SlideshowSelectEvent} */ (event).detail?.slide;
      this.#schedule(slide instanceof Element ? slide : undefined);
    }
  };

  /** @param {Event} event */
  #onLoad = (event) => {
    const gallery = this.#gallery;
    if (gallery && event.target instanceof HTMLImageElement && gallery.contains(event.target)) this.#schedule();
  };

  #onResize = () => {
    if (this.#resizeTimer != null) clearTimeout(this.#resizeTimer);
    this.#resizeTimer = setTimeout(() => {
      this.#resizeTimer = null;
      this.#schedule();
    }, 100);
  };

  #onVisibilityChange = () => {
    if (document.visibilityState !== 'visible' || !this.#pendingWhileHidden) return;
    this.#pendingWhileHidden = false;
    this.#schedule();
  };

  /** @type {Element | undefined} the slide announced by the last select event, read on the next frame. */
  #announcedSlide;

  /**
   * Coalesce every trigger into one measurement per frame. A slide announced by the
   * slideshow is decided on immediately — it is the backdrop about to be under the
   * mark, so waiting for the scroll to land would only delay the ink.
   *
   * @param {Element} [slide]
   */
  #schedule = (slide) => {
    if (slide instanceof Element) this.#announcedSlide = slide;
    if (this.#frame != null) return;
    if (document.visibilityState !== 'visible') {
      this.#pendingWhileHidden = true;
      return;
    }
    this.#frame = requestAnimationFrame(() => {
      this.#frame = null;
      const announced = this.#announcedSlide;
      this.#announcedSlide = undefined;
      this.#update(announced);
    });
  };

  /** @param {Element} [announcedSlide] */
  #update(announcedSlide) {
    const wordmark = this.#wordmark;
    if (!wordmark) return;
    const art = wordmark.querySelector(ART_SELECTOR) ?? wordmark;

    const gallery = this.#gallery;
    const slide = announcedSlide?.isConnected ? announcedSlide : currentSlide(gallery);
    const image = slide?.querySelector(SLIDE_IMAGE_SELECTOR);

    // Video, 3D, or no media at all: the declared over-artwork ink.
    if (!(image instanceof HTMLImageElement)) {
      this.#write(wordmark, ON_DARK);
      return;
    }

    const source = image.currentSrc || image.src;
    if (!source) return;

    const thumb = this.#thumbnail(source, image);
    if (!thumb) return; // decoding; #thumbnail re-schedules when it lands

    const markRect = art.getBoundingClientRect();
    const imageRect = landingRect(image, slide, gallery);
    if (markRect.width <= 0 || markRect.height <= 0 || imageRect.width <= 0 || imageRect.height <= 0) return;

    const fit = getComputedStyle(image).objectFit;
    const columns = clamp(Math.ceil(markRect.width / 120), 1, MAX_COLUMNS);

    let paper = 0;
    let total = 0;
    for (const row of ROWS) {
      const y = markRect.top + markRect.height * row;
      for (let i = 0; i < columns; i++) {
        const x = markRect.left + markRect.width * (columns === 1 ? 0.5 : 0.05 + (0.9 * i) / (columns - 1));
        total++;
        // Off the image, the mark sits on the page itself, which is paper.
        if (x < imageRect.left || x > imageRect.right || y < imageRect.top || y > imageRect.bottom) {
          paper++;
          continue;
        }
        const point = mapPointIntoSource(image, imageRect, fit, x, y);
        if (sampleLuminance(thumb, point) >= PAPER_LUMINANCE) paper++;
      }
    }

    const share = paper / total;
    const current = wordmark.dataset.rContrast;
    const threshold = current === ON_LIGHT ? PAPER_SHARE_LEAVE : PAPER_SHARE_ENTER;
    this.#write(wordmark, share >= threshold ? ON_LIGHT : ON_DARK);
  }

  /**
   * The reduced bitmap for a source, or null while it decodes. One decode per URL
   * for the life of the page; the promise is cached too, so a burst of triggers
   * cannot queue duplicates.
   *
   * @param {string} source
   * @param {HTMLImageElement} image
   * @returns {Uint8ClampedArray | null}
   */
  #thumbnail(source, image) {
    const cached = thumbnails.get(source);
    if (cached instanceof Uint8ClampedArray) return cached;
    if (cached) return null;

    const pending = decodeThumbnail(source).then((data) => {
      if (data) {
        if (thumbnails.size >= THUMB_CACHE_MAX) thumbnails.delete(thumbnails.keys().next().value);
        thumbnails.set(source, data);
        // Only worth a pass if this is still the image under the mark.
        if (image.isConnected && (image.currentSrc || image.src) === source) this.#schedule();
      } else {
        thumbnails.delete(source);
      }
      return data;
    });
    thumbnails.set(source, pending);
    return null;
  }

  /**
   * @param {HTMLElement} wordmark
   * @param {'on-dark' | 'on-light'} mode
   */
  #write(wordmark, mode) {
    if (wordmark.dataset.rContrast !== mode) wordmark.dataset.rContrast = mode;
  }
}

if (!customElements.get('r-adaptive-contrast')) {
  customElements.define('r-adaptive-contrast', AdaptiveContrast);
}

/**
 * The slide the slideshow says is current. Its own index is set before it announces
 * a selection; the `aria-hidden` attributes trail the scroll and are briefly true
 * for two slides at once, so they are only a fallback.
 *
 * @param {Element | null} gallery
 * @returns {Element | null | undefined}
 */
function currentSlide(gallery) {
  const slideshow = /** @type {{slides?: Element[], current?: number} | null} */ (gallery);
  return slideshow?.slides?.[slideshow.current ?? 0] ?? gallery?.querySelector(CURRENT_SLIDE_SELECTOR);
}

/**
 * The image's box once its slide has landed, not where it is mid-scroll. A decision
 * is made the moment a slide is announced, while it is still sliding in from the
 * side; read raw, its rect sits beside the mark and every probe misses it. Each
 * slide snaps to the scroller's origin, so shifting the image by the slide's
 * current offset from that origin gives the resting geometry at any scroll progress.
 *
 * @param {HTMLImageElement} image
 * @param {Element} slide
 * @param {Element | null} gallery
 * @returns {{left: number, top: number, right: number, bottom: number, width: number, height: number}}
 */
function landingRect(image, slide, gallery) {
  const raw = image.getBoundingClientRect();
  const slideRect = slide.getBoundingClientRect();
  const scrollerRect = gallery?.querySelector(SCROLLER_SELECTOR)?.getBoundingClientRect() ?? slideRect;
  const dx = scrollerRect.left - slideRect.left;
  const dy = scrollerRect.top - slideRect.top;
  return {
    left: raw.left + dx,
    top: raw.top + dy,
    right: raw.right + dx,
    bottom: raw.bottom + dy,
    width: raw.width,
    height: raw.height,
  };
}

/**
 * Decode a CORS-clean copy of the image (Shopify's CDN allows it) and reduce it to
 * a THUMB_SIZE square of RGBA. The squash is deliberate: probes are fractions of
 * the source in each axis, so aspect does not matter.
 *
 * @param {string} source
 * @returns {Promise<Uint8ClampedArray | null>}
 */
async function decodeThumbnail(source) {
  const image = new Image();
  image.crossOrigin = 'anonymous';
  image.src = source;
  try {
    await image.decode();
    const canvas = document.createElement('canvas');
    canvas.width = canvas.height = THUMB_SIZE;
    const context = canvas.getContext('2d', { willReadFrequently: true });
    if (!context) return null;
    context.drawImage(image, 0, 0, THUMB_SIZE, THUMB_SIZE);
    return context.getImageData(0, 0, THUMB_SIZE, THUMB_SIZE).data;
  } catch {
    // Decode failure or a tainted canvas: no answer, keep the current ink.
    return null;
  }
}

/**
 * A viewport point as a FRACTION of the bitmap, honouring how `object-fit` places
 * the image inside its box. Fractions rather than pixels: `naturalWidth` of a
 * `srcset` image is density-corrected and does not match the decoded bitmap.
 *
 * Assumes the theme's centred `object-position`.
 *
 * @param {HTMLImageElement} image
 * @param {{left: number, top: number, width: number, height: number}} rect
 * @param {string} fit
 * @param {number} x
 * @param {number} y
 * @returns {{x: number, y: number}}
 */
function mapPointIntoSource(image, rect, fit, x, y) {
  const { naturalWidth: width, naturalHeight: height } = image;
  if (!width || !height) return { x: clamp((x - rect.left) / rect.width, 0, 1), y: clamp((y - rect.top) / rect.height, 0, 1) };

  let scaleX = rect.width / width;
  let scaleY = rect.height / height;
  if (fit === 'contain' || fit === 'scale-down') scaleX = scaleY = Math.min(scaleX, scaleY);
  else if (fit === 'cover') scaleX = scaleY = Math.max(scaleX, scaleY);
  else if (fit === 'none') scaleX = scaleY = 1;

  const originX = rect.left + (rect.width - width * scaleX) / 2;
  const originY = rect.top + (rect.height - height * scaleY) / 2;
  return {
    x: clamp((x - originX) / scaleX / width, 0, 1),
    y: clamp((y - originY) / scaleY / height, 0, 1),
  };
}

/**
 * Mean relative luminance of the 3x3 thumbnail cells around a fractional point.
 *
 * @param {Uint8ClampedArray} data
 * @param {{x: number, y: number}} point
 */
function sampleLuminance(data, point) {
  const cx = Math.round(point.x * (THUMB_SIZE - 1));
  const cy = Math.round(point.y * (THUMB_SIZE - 1));
  let total = 0;
  let count = 0;
  for (let y = Math.max(0, cy - 1); y <= Math.min(THUMB_SIZE - 1, cy + 1); y++) {
    for (let x = Math.max(0, cx - 1); x <= Math.min(THUMB_SIZE - 1, cx + 1); x++) {
      const i = (y * THUMB_SIZE + x) * 4;
      total += relativeLuminance(data[i], data[i + 1], data[i + 2]);
      count++;
    }
  }
  return total / count;
}

/**
 * WCAG relative luminance, 0 (black) to 1 (white).
 *
 * @param {number} r
 * @param {number} g
 * @param {number} b
 */
function relativeLuminance(r, g, b) {
  const channel = (value) => {
    const c = value / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}

/**
 * @param {number} value
 * @param {number} min
 * @param {number} max
 */
function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}
